import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';

const CHAVE_EMAIL_LEMBRADO = 'bravobet_email_lembrado';

const Login: React.FC = () => {
  const [email, setEmail] = useState(() => localStorage.getItem(CHAVE_EMAIL_LEMBRADO) || '');
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [sessaoExpirada, setSessaoExpirada] = useState(false);
  // Só o email é lembrado (nunca a senha) — persistir senha em texto no
  // navegador seria um risco de segurança real, mesmo sendo conveniente.
  const [lembrarMe, setLembrarMe] = useState(() => !!localStorage.getItem(CHAVE_EMAIL_LEMBRADO));

  const { login, usuario } = useAuth();
  const { tema, alternarTema } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Mostra um aviso explícito quando a chegada aqui foi forçada por um
  // token expirado (ver interceptor de resposta 401 em services/api.ts) —
  // antes isso acontecia em silêncio, parecendo um recarregamento aleatório.
  useEffect(() => {
    if (sessionStorage.getItem('sessaoExpirada')) {
      sessionStorage.removeItem('sessaoExpirada');
      setSessaoExpirada(true);
    }
  }, []);

  // Redirecionar se já estiver autenticado — respeitando ?next= quando o
  // usuário chegou aqui tentando abrir um card/aviso/tópico específico
  // (link de email) sem sessão ativa.
  useEffect(() => {
    if (usuario) {
      // URLSearchParams já decodifica o valor uma vez — não decodificar de novo aqui.
      const params = new URLSearchParams(location.search);
      const destino = params.get('next');
      // Só aceita caminhos internos (defesa contra open redirect).
      if (destino && destino.startsWith('/') && !destino.startsWith('//')) {
        navigate(destino, { replace: true });
        return;
      }
      navigate('/');
    }
  }, [usuario, navigate, location.search]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !senha) {
      setErro('Por favor, preencha todos os campos');
      return;
    }
    
    setErro('');
    setCarregando(true);
    
    try {
      const sucesso = await login(email, senha);

      if (sucesso) {
        if (lembrarMe) {
          localStorage.setItem(CHAVE_EMAIL_LEMBRADO, email);
        } else {
          localStorage.removeItem(CHAVE_EMAIL_LEMBRADO);
        }
        // Login bem-sucedido, o contexto já atualizou o usuário
        // Verificar tipo de usuário para redirecionar
        // O usuário será atualizado pelo useEffect
      } else {
        setErro('Email ou senha inválidos');
      }
    } catch (error) {
      setErro('Ocorreu um erro ao fazer login. Tente novamente.');
      console.error('Erro de login:', error);
    } finally {
      setCarregando(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8 relative">
      <button
        onClick={alternarTema}
        className="absolute top-4 right-4 flex items-center justify-center h-10 w-10 rounded-full bg-white dark:bg-gray-800 shadow-md text-bravo-secondary dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        title={tema === 'escuro' ? 'Ativar modo claro' : 'Ativar modo escuro'}
        aria-label={tema === 'escuro' ? 'Ativar modo claro' : 'Ativar modo escuro'}
      >
        {tema === 'escuro' ? (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
          </svg>
        )}
      </button>

      <div className="max-w-md w-full space-y-8 bg-white dark:bg-gray-800 p-8 rounded-lg shadow-md">
        <div>
          <div className="flex justify-center mb-4">
            <img src="/favicon.png" alt="BravoHub Logo" className="h-16 w-16" />
          </div>
          <h2 className="mt-2 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
            BravoHub
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
            Faça login para acessar o sistema
          </p>
        </div>

        {sessaoExpirada && (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 text-sm rounded-md p-3 text-center">
            Sua sessão expirou. Faça login novamente para continuar de onde parou.
          </div>
        )}

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">Email</label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-bravo-primary focus:border-bravo-primary focus:z-10 sm:text-sm"
                placeholder="Email"
              />
            </div>
            <div>
              <label htmlFor="senha" className="sr-only">Senha</label>
              <input
                id="senha"
                name="senha"
                type="password"
                autoComplete="current-password"
                required
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-bravo-primary focus:border-bravo-primary focus:z-10 sm:text-sm"
                placeholder="Senha"
              />
            </div>
          </div>

          {erro && (
            <div className="text-red-500 text-sm text-center">
              {erro}
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="lembrar"
                name="lembrar"
                type="checkbox"
                checked={lembrarMe}
                onChange={(e) => setLembrarMe(e.target.checked)}
                className="h-4 w-4 text-bravo-primary focus:ring-bravo-primary border-gray-300 rounded"
              />
              <label htmlFor="lembrar" className="ml-2 block text-sm text-gray-900">
                Lembrar-me
              </label>
            </div>

            <div className="text-sm">
              <a href="#" className="font-medium text-bravo-primary hover:text-bravo-primary">
                Esqueceu sua senha?
              </a>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={carregando}
              className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-bravo-primary hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bravo-primary ${carregando ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {carregando ? 'Entrando...' : 'Entrar'}
            </button>
          </div>
          
          {/* Dica de login removida */}
        </form>
      </div>
    </div>
  );
};

export default Login;
